import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  Circle,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Page, PageHeader, Empty } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const PRIORITIES = ["Low", "Normal", "High", "Urgent"];
const blank = {
  title: "",
  priority: "Normal",
  due_date: "",
  reminder_at: "",
  notes: "",
};
const displayDate = (value) =>
  new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
const displayDateTime = (value) =>
  new Date(value).toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

const toLocalDateTimeValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 16);
  const pad = (number) => String(number).padStart(2, "0");
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join("-") + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toReminderPayload = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
};

const priorityClass = (priority) =>
  ({
    Urgent: "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300",
    High: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
    Normal: "bg-muted text-muted-foreground",
    Low: "bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300",
  })[priority];

function TaskDialog({ task, onClose, onSaved }) {
  const [form, setForm] = useState({ ...blank, ...task });
  const [busy, setBusy] = useState(false);
  const minimumReminderTime = useMemo(
    () => toLocalDateTimeValue(new Date(Date.now() + 16 * 60 * 1000)),
    [],
  );
  const reminderTime = new Date(toReminderPayload(form.reminder_at)).getTime();
  const reminderTooSoon = Boolean(
    form.reminder_at && (
      Number.isNaN(reminderTime) || reminderTime < Date.now() + 15 * 60 * 1000
    ),
  );
  const set = (key) => (value) =>
    setForm((current) => ({ ...current, [key]: value }));
  const save = async () => {
    if (reminderTooSoon) return;
    setBusy(true);
    try {
      const payload = { ...form, reminder_at: toReminderPayload(form.reminder_at) };
      if (task?.id) {
        await api.put(`/technician-tasks/${task.id}`, payload);
        toast.success("To-do updated");
      } else {
        await api.post("/technician-tasks", payload);
        toast.success("Added to your to-do list");
      }
      onSaved();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="technician-task-dialog">
        <DialogHeader>
          <DialogTitle>{task?.id ? "Edit to-do" : "Add to-do"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(event) => set("title")(event.target.value)}
              data-testid="technician-task-title-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={set("priority")}>
              <SelectTrigger data-testid="technician-task-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((priority) => (
                  <SelectItem key={priority} value={priority}>
                    {priority}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Due date</Label>
            <Input
              type="date"
              value={form.due_date}
              onChange={(event) => set("due_date")(event.target.value)}
              data-testid="technician-task-due-date-input"
            />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="technician-task-reminder-input">Reminder (optional)</Label>
            <Input
              id="technician-task-reminder-input"
              type="datetime-local"
              min={minimumReminderTime}
              value={toLocalDateTimeValue(form.reminder_at)}
              onChange={(event) => set("reminder_at")(event.target.value)}
              data-testid="technician-task-reminder-input"
            />
            <p
              className="text-xs text-muted-foreground"
              data-testid="technician-task-reminder-help"
            >
              In your device&apos;s local time. Choose a time at least 15 minutes away; the bell
              alert arrives within 15 minutes after it is due.
            </p>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>Notes</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(event) => set("notes")(event.target.value)}
              data-testid="technician-task-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="technician-task-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy || !form.title.trim() || reminderTooSoon}
            data-testid="technician-task-save-button"
          >
            {busy ? "Saving…" : "Save to-do"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskRow({ task, onEdit, onComplete, onReopen, onDelete }) {
  const isComplete = task.status === "Completed";
  const isOverdue =
    !isComplete &&
    task.due_date &&
    task.due_date < new Date().toISOString().slice(0, 10);
  return (
    <div
      className="border-b last:border-b-0 px-4 py-4"
      data-testid={`technician-task-row-${task.id}`}
    >
      <div className="flex gap-3">
        <button
          className="mt-0.5 shrink-0 text-primary hover:text-primary/75"
          onClick={() => (isComplete ? onReopen(task) : onComplete(task))}
          title={isComplete ? "Reopen task" : "Mark task complete"}
          data-testid={`technician-task-toggle-${task.id}`}
        >
          {isComplete ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <Circle className="h-5 w-5" />
          )}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2
              className={`font-semibold ${isComplete ? "text-muted-foreground line-through" : ""}`}
              data-testid={`technician-task-title-${task.id}`}
            >
              {task.title}
            </h2>
            <span
              className={
                `rounded-full px-2 py-0.5 text-[11px] font-medium ${priorityClass(task.priority)}`
              }
              data-testid={`technician-task-priority-${task.id}`}
            >
              {task.priority}
            </span>
            {isOverdue && (
              <span
                className={
                  "rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium " +
                  "text-rose-800 dark:bg-rose-950/60 dark:text-rose-300"
                }
                data-testid={`technician-task-overdue-${task.id}`}
              >
                Overdue
              </span>
            )}
          </div>
          {task.due_date && (
            <p
              className="mt-1 text-sm text-muted-foreground"
              data-testid={`technician-task-due-${task.id}`}
            >
              {isComplete ? "Completed" : "Due"}{" "}
              {displayDate(isComplete ? task.completed_at : task.due_date)}
            </p>
          )}
          {!isComplete && task.reminder_at && (
            <p
              className="mt-1 text-sm text-muted-foreground"
              data-testid={`technician-task-reminder-${task.id}`}
            >
              Reminder {displayDateTime(task.reminder_at)}
            </p>
          )}
          {task.notes && (
            <p
              className="mt-2 whitespace-pre-wrap text-sm"
              data-testid={`technician-task-notes-${task.id}`}
            >
              {task.notes}
            </p>
          )}
          {task.source === "jonny" && (
            <p
              className="mt-2 text-xs text-muted-foreground"
              data-testid={`technician-task-source-${task.id}`}
            >
              Added via Jonny
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-start gap-1">
          {isComplete ? (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onReopen(task)}
              title="Reopen task"
              data-testid={`technician-task-reopen-${task.id}`}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onEdit(task)}
              title="Edit task"
              data-testid={`technician-task-edit-${task.id}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onDelete(task)}
            title="Delete task"
            data-testid={`technician-task-delete-${task.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function TechnicianTasksPage() {
  const [tab, setTab] = useState("open");
  const [tasks, setTasks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const requestId = useRef(0);
  const load = useCallback(() => {
    const currentRequest = ++requestId.current;
    api
      .get("/technician-tasks", { params: { status: tab } })
      .then((response) => {
        if (currentRequest === requestId.current) setTasks(response.data);
      })
      .catch(() => {});
  }, [tab]);
  useEffect(() => {
    load();
  }, [load]);
  const complete = async (task) => {
    try {
      await api.post(`/technician-tasks/${task.id}/complete`);
      toast.success("Task completed");
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const reopen = async (task) => {
    try {
      await api.post(`/technician-tasks/${task.id}/reopen`);
      toast.success("Task reopened");
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/technician-tasks/${deleting.id}`);
      toast.success("To-do removed");
      setDeleting(null);
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const sorted = useMemo(
    () =>
      [...tasks].sort((left, right) => {
        if (tab === "completed")
          return String(right.completed_at).localeCompare(
            String(left.completed_at),
          );
        const priority = { Urgent: 0, High: 1, Normal: 2, Low: 3 };
        return (
          priority[left.priority] - priority[right.priority] ||
          String(left.due_date || "9999").localeCompare(
            String(right.due_date || "9999"),
          )
        );
      }),
    [tab, tasks],
  );

  return (
    <Page wide>
      <PageHeader
        title="My to-do list"
        subtitle={
          "Your tasks, separate from practical preparation. "
          + "Set reminders at least 15 minutes in advance."
        }
      >
        <Button
          onClick={() => setEditing({})}
          data-testid="technician-task-add-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add to-do
        </Button>
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="open" data-testid="technician-task-tab-open">
            Open
          </TabsTrigger>
          <TabsTrigger
            value="completed"
            data-testid="technician-task-tab-completed"
          >
            Completed history
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {sorted.length === 0 ? (
        <Empty>
          {tab === "open"
            ? "No personal to-dos right now."
            : "No completed to-dos yet."}
        </Empty>
      ) : (
        <div
          className="rounded-xl border bg-card"
          data-testid="technician-task-list"
        >
          {sorted.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              onEdit={setEditing}
              onComplete={complete}
              onReopen={reopen}
              onDelete={setDeleting}
            />
          ))}
        </div>
      )}
      {editing && (
        <TaskDialog
          task={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
      <AlertDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialogContent data-testid="technician-task-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This personal to-do will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="technician-task-delete-cancel">
              Keep
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              data-testid="technician-task-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
